import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types'; // Import PropTypes
import { useDarkMode } from '../contexts/DarkModeContext';

const Dropdown = ({ parentStyle, heading = {}, items, type = 'button', onSelectionChange, customStyle }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropDirection, setDropDirection] = useState("down");
    const [selectedItems, setSelectedItems] = useState([]);
    const { isDarkMode } = useDarkMode();
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Generate a unique ID for the dropdown using a CSPRNG
    const dropdownId = useMemo(() => {
        const array = new Uint32Array(1);
        window.crypto.getRandomValues(array);
        return `dropdownMenuButton-${array[0].toString(36)}`;
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Handle dropdown position
    useEffect(() => {
        const handlePosition = () => {
            if (buttonRef.current && isOpen) {
                const rect = buttonRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const spaceAbove = rect.top;

                const dropdownHeight = 250;

                if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                    setDropDirection("up");
                } else {
                    setDropDirection("down");
                }
            }
        };

        handlePosition();
        window.addEventListener("resize", handlePosition);
        return () => window.removeEventListener("resize", handlePosition);
    }, [isOpen]);

    const handleCheckboxChange = (item) => {
        const updatedSelection = selectedItems.includes(item)
            ? selectedItems.filter((i) => i !== item)
            : [...selectedItems, item];

        setSelectedItems(updatedSelection);
        if (onSelectionChange) {
            onSelectionChange(updatedSelection);
        }
    };

    const isAllSelected = items.length > 0 && selectedItems.length === items.length;

    const handleSelectAllChange = (isChecked) => {
        const updatedSelection = isChecked ? items : [];
        setSelectedItems(updatedSelection);
        if (onSelectionChange) {
            onSelectionChange(updatedSelection);
        }
    };

    const truncateName = (name, maxLength = 20) => {
        return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleItemClick = (item) => {
        if (item.onClick) {
            item.onClick();
        }
        if (type === 'button') {
            setIsOpen(false);
        }
    };

    // Determine if we need scrolling (more than 3 items)
    const needsScrolling = items.length > 3;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                ref={buttonRef}
                type="button"
                id={dropdownId}
                onClick={toggleDropdown}
                className="inline-flex items-center justify-between gap-2 whitespace-nowrap"
                style={customStyle}
            >
                {heading.icon && <span>{heading.icon}</span>}
                {heading.name && <span>{truncateName(heading.name)}</span>}
                <svg 
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            
            {isOpen && (
                <div 
                    className={`absolute z-50 mt-2 rounded-xl ${
                        dropDirection === 'up' ? 'bottom-full mb-2' : 'top-full'
                    }`}
                    style={{
                        background: isDarkMode ? '#1e293b' : '#ffffff',
                        border: isDarkMode ? '1px solid rgba(71, 85, 105, 0.6)' : '1px solid rgba(0, 0, 0, 0.15)',
                        boxShadow: isDarkMode 
                            ? '0 4px 16px rgba(0, 0, 0, 0.4)' 
                            : '0 4px 16px rgba(0, 0, 0, 0.1)',
                        minWidth: '100%',
                        width: 'max-content',
                    }}
                >
                    <div 
                        className={`p-2 ${needsScrolling ? 'overflow-y-auto' : ''}`}
                        style={{
                            maxHeight: needsScrolling ? '200px' : 'none',
                        }}
                    >
                        {items.length === 0 ? (
                            <div 
                                className="px-3 py-2 text-sm"
                                style={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                            >
                                No data
                            </div>
                        ) : (
                            <>
                                {type === 'checklist' && (
                                    <div>
                                        <label
                                            className="flex items-center px-3 py-2 rounded-lg cursor-pointer transition-colors"
                                            style={{
                                                color: isDarkMode ? '#e2e8f0' : '#334155',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = isDarkMode ? 'rgba(51, 65, 85, 0.5)' : 'rgba(248, 250, 252, 0.9)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <input
                                                className="mr-2"
                                                type="checkbox"
                                                id={`select-all-${dropdownId}`}
                                                checked={isAllSelected}
                                                onChange={(e) => handleSelectAllChange(e.target.checked)}
                                            />
                                            <span className="text-sm">Select All</span>
                                        </label>
                                    </div>
                                )}
                                {items.map((item, index) => (
                                    <div key={item.name || index}>
                                        {type === 'checklist' ? (
                                            <label
                                                className="flex items-center px-3 py-2 rounded-lg cursor-pointer transition-colors"
                                                style={{
                                                    color: isDarkMode ? '#e2e8f0' : '#334155',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = isDarkMode ? 'rgba(51, 65, 85, 0.5)' : 'rgba(248, 250, 252, 0.9)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'transparent';
                                                }}
                                            >
                                                <input
                                                    className="mr-2"
                                                    type="checkbox"
                                                    id={`checkbox-${dropdownId}-${item.name}`}
                                                    checked={selectedItems.includes(item)}
                                                    onChange={() => handleCheckboxChange(item)}
                                                />
                                                <span className="text-sm" title={item.name}>
                                                    {item.icon && <span className="mr-1">{item.icon}</span>}
                                                    {truncateName(item.name)}
                                                </span>
                                            </label>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleItemClick(item)}
                                                title={item.name}
                                                disabled={!!item.disabled}
                                                className="w-full text-left flex items-center px-3 py-2.5 rounded-lg transition-all text-sm border-0 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{
                                                    color: isDarkMode ? '#e2e8f0' : '#334155',
                                                    background: item.isSelected 
                                                        ? (isDarkMode ? 'rgba(14, 165, 233, 0.15)' : 'rgba(14, 165, 233, 0.08)')
                                                        : 'transparent',
                                                    fontWeight: item.isSelected ? '600' : '400',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!item.isSelected && !item.disabled) {
                                                        e.currentTarget.style.background = isDarkMode ? 'rgba(51, 65, 85, 0.5)' : 'rgba(248, 250, 252, 0.9)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!item.isSelected) {
                                                        e.currentTarget.style.background = 'transparent';
                                                    } else {
                                                        e.currentTarget.style.background = isDarkMode ? 'rgba(14, 165, 233, 0.15)' : 'rgba(14, 165, 233, 0.08)';
                                                    }
                                                }}
                                            >
                                                {item.icon && <span className="flex items-center">{item.icon}</span>}
                                                <span>{item.name && truncateName(item.name)}</span>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Define PropTypes for Dropdown
Dropdown.propTypes = {
    parentStyle: PropTypes.string,
    heading: PropTypes.shape({
        icon: PropTypes.node,
        name: PropTypes.string
    }),
    items: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.node,
            name: PropTypes.string.isRequired,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            isSelected: PropTypes.bool
        })
    ).isRequired,
    type: PropTypes.oneOf(['button', 'checklist']),
    onSelectionChange: PropTypes.func,
    customStyle: PropTypes.object
};

Dropdown.defaultProps = {
    parentStyle: '',
    heading: {},
    type: 'button',
    onSelectionChange: null,
    customStyle: null
};

export default Dropdown;